/*
    Copyright 2022-2025, Techwise Integrated Security, LLC
*/

import React, { useState, useEffect } from "react";
import { Alert, Box, Button, TextField, Grid, CircularProgress, Stack, Typography } from "@mui/material";

import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import Autocomplete from "@mui/material/Autocomplete";

import { Material, Nullable, StorageLocation, TechwiseOffice, TechwiseOfficeDetailed } from "../../utils/Interfaces";

import { selectExistingTechwiseOffices } from "../materials-techwise-offices/techwiseOfficesSlice";
import { techwiseOfficeDetails } from "../materials-techwise-office/techwiseOfficeFunctions";
import { updateTechwiseOffices } from "../materials-techwise-offices/techwiseOfficesFunctions";
import { NumericFormat } from "react-number-format";
import { CompareArrows } from "@mui/icons-material";
import { inventoryIngress } from "./materialTransferFunctions";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import DeferredAttachmentManager from "../../components/general/DeferredAttachmentManager";
import { ModelName } from "../../utils/Requests";
import { delayedAction } from "../../utils/Functions";
import AnimatedCompletionCheck from "../../components/confirmation/AnimatedCompletionCheck";
import { useMaterialsDatabase } from "../materials-manager/materialsHooks";

const InventoryIngress = () => {
    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const [selectedTechwiseOffice, setSelectedTechwiseOffice] = useState<Nullable<TechwiseOffice>>(null);

    const [storageLocations, setStorageLocations] = useState<StorageLocation[]>([]);
    const [selectedStorageLocation, setSelectedStorageLocation] = useState<Nullable<StorageLocation>>(null);

    const techwiseOffices: TechwiseOffice[] = useAppSelector(selectExistingTechwiseOffices);

    const { data: materials } = useMaterialsDatabase();

    const [selectedMaterial, setSelectedMaterial] = useState<Nullable<Material>>(null);

    const [officesLoaded, setOfficesLoaded] = useState<boolean>(false);

    const [loading, setLoading] = useState<boolean>(true);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [complete, setComplete] = useState<boolean>(false);

    const [amount, setAmount] = useState<number>(0);
    const [transferNotes, setTransferNotes] = useState<string>("");

    const [inventoryTransferId, setInventoryTransferId] = useState<Nullable<number>>(null);

    function getFormErrors(): string {
        if (techwiseOffices.length === 0) {
            return "No Techwise offices available.";
        }

        if (storageLocations.length === 0) {
            return "The selected Techwise office has no storage locations available.";
        }

        if (selectedMaterial === null) {
            return "Please select a material.";
        }

        if (amount === null || amount < 1 || isNaN(amount)) {
            return "Amount to transfer must be at least 1.";
        }

        return "";
    }

    // Logic to disable the complete check after a set period of time.
    useEffect(() => {
        if (complete) {
            delayedAction(2000, () => setComplete(false));
        }
    }, [complete]);

    useEffect(() => {
        setLoading(true);
        if (techwiseOffices.length > 0) {
            setSelectedTechwiseOffice(techwiseOffices[0]);
        } else {
            // If the count of offices is 0, attempt a single reload. Use a Boolean flag to prevent recursive requests.
            if (!officesLoaded) {
                setOfficesLoaded(true);
                updateTechwiseOffices(dispatch, () => {});
            }
        }
    }, [techwiseOffices]);

    useEffect(() => {
        setSelectedStorageLocation(null);
        if (selectedTechwiseOffice !== null) {
            techwiseOfficeDetails(selectedTechwiseOffice?.id, dispatch, (techwiseOfficeDetailed: TechwiseOfficeDetailed) => {
                setStorageLocations(techwiseOfficeDetailed.locations);
                if (techwiseOfficeDetailed.locations.length > 0) {
                    setSelectedStorageLocation(techwiseOfficeDetailed.locations[0]);
                }
                setLoading(false);
            });
        }
    }, [selectedTechwiseOffice]);

    if (loading) {
        return (
            <Box sx={{ mt: 5 }}>
                <Stack direction="row" justifyContent="center" sx={{ mt: 15, mb: 10 }}>
                    <CircularProgress size={40} />
                </Stack>
            </Box>
        );
    }

    return (
        <Box>
            {getFormErrors() === "" && (
                <Alert severity="success" sx={{ mt: 2 }}>
                    All entries are valid.
                </Alert>
            )}
            {getFormErrors() !== "" && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {getFormErrors()}
                </Alert>
            )}
            <Grid spacing={3} sx={{ mt: 3 }} container>
                <Grid item xs={6}>
                    <Autocomplete
                        disablePortal
                        onChange={(_event, value) => {
                            setSelectedTechwiseOffice(value ? value : techwiseOffices.length > 0 ? techwiseOffices[0] : null);
                        }}
                        id="techwiseOffices"
                        getOptionKey={(option: TechwiseOffice) => option.id}
                        options={techwiseOffices}
                        value={selectedTechwiseOffice}
                        getOptionLabel={(option: TechwiseOffice) => option.name}
                        renderInput={(params) => <TextField required {...params} label="Techwise Office" />}
                    />
                </Grid>
                <Grid item xs={6}>
                    <Autocomplete
                        disablePortal
                        onChange={(_event, value) => {
                            setSelectedStorageLocation(value ? value : storageLocations.length > 0 ? storageLocations[0] : null);
                        }}
                        id="storageLocations"
                        getOptionKey={(option: StorageLocation) => option.id}
                        options={storageLocations}
                        value={selectedStorageLocation}
                        getOptionLabel={(option: StorageLocation) => option.name}
                        renderInput={(params) => <TextField required {...params} label="Storage Locations" />}
                    />
                </Grid>
            </Grid>

            <Grid spacing={3} sx={{ mt: 3 }} container>
                <Grid item xs={6}>
                    <Autocomplete
                        disablePortal
                        onChange={(_event, value) => {
                            setSelectedMaterial(
                                value ? value : (materials?.results.length ?? 0) > 0 ? (materials?.results[0] as Material) : null
                            );
                        }}
                        id="materials"
                        options={materials?.results ?? []}
                        filterOptions={(options: Material[], state) =>
                            options.filter(
                                (option) =>
                                    option.name.toLowerCase().includes(state.inputValue.toLowerCase()) ||
                                    option.part_number.toLowerCase().includes(state.inputValue.toLowerCase())
                            )
                        }
                        value={selectedMaterial}
                        getOptionLabel={(option: Material) => `${option.name} (${option.part_number})`}
                        getOptionKey={(option: Material) => option.id}
                        renderInput={(params) => (
                            <TextField required {...params} label="Material" helperText="Material Name (Part #)" />
                        )}
                    />
                </Grid>
                <Grid item xs={6}>
                    <NumericFormat
                        value={amount}
                        customInput={TextField}
                        style={{ marginLeft: 0, marginTop: 0, width: "100%" }}
                        label="Amount"
                        thousandSeparator=","
                        decimalScale={0}
                        allowNegative={false}
                        allowLeadingZeros={false}
                        onValueChange={(values) => {
                            setAmount(parseInt(values.value));
                        }}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        sx={{ mt: 3 }}
                        value={transferNotes}
                        onChange={(event) => setTransferNotes(event.target.value)}
                        label="Transfer Notes"
                        name="transfer_notes"
                    />
                </Grid>
                <Grid item xs={6}>
                    <DeferredAttachmentManager
                        model={ModelName.InventoryTransfer}
                        idKey={inventoryTransferId}
                        readOnly={false}
                        editing={true}
                        onFilesUploaded={() => {
                            setComplete(true);
                            setSelectedMaterial(null);
                            setAmount(0);
                            setTransferNotes("");
                            setSubmitting(false);
                            setInventoryTransferId(null);
                        }}
                    />
                </Grid>
            </Grid>

            {submitting && <CircularProgress size={16} sx={{ mt: 6, ml: 2, mb: 0.8 }} />}

            {!submitting && (
                <Stack direction="row" justifyContent="flex-start" sx={{ mt: 5 }}>
                    <Button
                        variant="contained"
                        disabled={getFormErrors() !== "" || submitting}
                        onClick={() => {
                            setSubmitting(true);
                            inventoryIngress(
                                selectedStorageLocation as StorageLocation,
                                selectedMaterial as Material,
                                amount,
                                transferNotes,
                                dispatch,
                                (createdTransferId: number) => setInventoryTransferId(createdTransferId),
                                () => setSubmitting(false)
                            );
                        }}
                    >
                        <CompareArrows />
                        <Typography sx={{ ml: 1 }}>Initiate Transfer</Typography>
                    </Button>
                    {complete && (
                        <Stack direction="row" justifyContent="flex-start" sx={{ mt: 1, ml: 3 }}>
                            <AnimatedCompletionCheck size={24} />
                            <Typography color="success.main" sx={{ ml: 2, mt: -0.35 }} variant="h6">
                                Ingress Successful
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            )}
        </Box>
    );
};

export default InventoryIngress;
